import { computed, ref, watch } from 'vue'
// import { useStore } from 'vuex'
import {
  useMonthlyConfigurationFilters,
  buildEarnDeductFilterContext,
  applyEarnDeductFilterContext,
  clearFilterEntityFields
} from './useMonthlyConfigurationFilters.js'

const STORAGE_KEY = 'finance_earn_deduct_monthly_config_v1'

function normalizePeriodBundle(raw) {
  if (Array.isArray(raw)) {
    return { entries: raw, filters: null }
  }
  if (!raw || typeof raw !== 'object') {
    return { entries: [], filters: null }
  }
  return {
    entries: Array.isArray(raw.entries) ? raw.entries : [],
    filters: raw.filters && typeof raw.filters === 'object' ? raw.filters : null
  }
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = JSON.parse(raw || '{}')
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }
    const normalized = {}
    for (const [key, value] of Object.entries(parsed)) {
      const bundle = normalizePeriodBundle(value)
      if (bundle.entries.length > 0) {
        normalized[key] = bundle
      }
    }
    return normalized
  } catch {
    return {}
  }
}

function persistToStorage(data) {
  const cleaned = {}
  for (const [key, value] of Object.entries(data || {})) {
    const bundle = normalizePeriodBundle(value)
    if (bundle.entries.length > 0) {
      cleaned[key] = bundle
    }
  }
  if (Object.keys(cleaned).length === 0) {
    localStorage.removeItem(STORAGE_KEY)
    return
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned))
}

const periodsByKey = ref(loadFromStorage())
let storageWatcherInitialized = false
let periodRestoreInitialized = false

function initStorageWatcher() {
  if (storageWatcherInitialized) return
  storageWatcherInitialized = true
  watch(
    periodsByKey,
    (val) => {
      persistToStorage(val)
    },
    { deep: true }
  )
}

function updatePeriodBundle(periodKey, bundle) {
  if (!periodKey) return
  const normalized = normalizePeriodBundle(bundle)
  const next = { ...periodsByKey.value }
  if (normalized.entries.length === 0) {
    delete next[periodKey]
  } else {
    next[periodKey] = normalized
  }
  periodsByKey.value = next
}

function updatePeriodEntries(periodKey, entries, existingFilters = undefined) {
  if (!periodKey) return
  const current = normalizePeriodBundle(periodsByKey.value[periodKey])
  const filters = existingFilters !== undefined ? existingFilters : current.filters
  updatePeriodBundle(periodKey, { entries, filters })
}

export function resolveEarnDeductPeriodKey(filterForm) {
  if (filterForm?.selectedMonth) {
    return `month:${filterForm.selectedMonth}`
  }
  if (filterForm?.fromDate && filterForm?.toDate) {
    return `range:${filterForm.fromDate}:${filterForm.toDate}`
  }
  return null
}

export function persistEarnDeductPeriodFilters(filterForm) {
  const periodKey = resolveEarnDeductPeriodKey(filterForm)
  if (!periodKey) return
  const current = normalizePeriodBundle(periodsByKey.value[periodKey])
  updatePeriodBundle(periodKey, {
    entries: current.entries,
    filters: buildEarnDeductFilterContext(filterForm)
  })
}

export function clearEarnDeductCurrentPeriod(filterForm) {
  const periodKey = resolveEarnDeductPeriodKey(filterForm)
  if (!periodKey) return
  updatePeriodBundle(periodKey, { entries: [], filters: null })
  clearFilterEntityFields()
}

async function restorePeriodState(periodKey) {
  if (!periodKey) {
    clearFilterEntityFields()
    return
  }
  const bundle = normalizePeriodBundle(periodsByKey.value[periodKey])
  if (!bundle.entries.length) {
    // No in-progress draft for this period — keep the user's current entity filters.
    return
  }
  if (bundle.filters) {
    await applyEarnDeductFilterContext(bundle.filters)
  }
}

export function useEarnDeductLocalStorage() {
  initStorageWatcher()

  const { form: filterForm } = useMonthlyConfigurationFilters()

  const currentPeriodKey = computed(() => resolveEarnDeductPeriodKey(filterForm))

  if (!periodRestoreInitialized) {
    periodRestoreInitialized = true
    watch(
      currentPeriodKey,
      async (key, prevKey) => {
        if (key === prevKey) return
        await restorePeriodState(key)
      }
    )
  }

  const localEntries = computed({
    get() {
      const key = currentPeriodKey.value
      if (!key) return []
      return normalizePeriodBundle(periodsByKey.value[key]).entries
    },
    set(nextEntries) {
      const key = currentPeriodKey.value
      if (!key) return
      const current = normalizePeriodBundle(periodsByKey.value[key])
      updatePeriodEntries(key, Array.isArray(nextEntries) ? nextEntries : [], current.filters)
    }
  })

  const filtersLocked = computed(() => localEntries.value.length > 0)

  return {
    filterForm,
    currentPeriodKey,
    periodsByKey,
    localEntries,
    filtersLocked,
    clearCurrentPeriod: () => clearEarnDeductCurrentPeriod(filterForm)
  }
}
